import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { HTTPError } from "griptonite-api-client/client";
import { ApiCreditProductAdmin, ApiCreditSystemAdmin } from "griptonite-api-client/types";
import { ReactNode, useMemo, useState } from "react";
import { FormProvider, useController, useForm, useFormContext } from "react-hook-form";

import {
  useCreateCreditProductMutation,
  useCreditProductPresetImagesQuery,
  useCreditProductQuery,
  useDeleteCreditProductMutation,
  useUpdateCreditProductMutation,
  useVenueCreditSystemQuery,
} from "@/hooks/credits";
import { useCurrentVenueQuery } from "@/hooks/venues";
import formatCurrency from "@/utils/formatCurrency";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import TextInput, { NumberInput, TextareaInput } from "../forms/TextInput";
import { REWARD_CATEGORIES, REWARD_CATEGORY_LABELS } from "./rewardCategories";
import {
  RewardFormSchema,
  RewardFormValues,
  rewardFormValuesToPayload,
  rewardToFormValues,
} from "./rewardForms";
import RewardImageInput from "./RewardImageInput";
import RewardStock from "./RewardStock";
import RewardSummary from "./RewardSummary";
import { UnsavedChangesProvider, useUnsavedChanges } from "./UnsavedChanges";

const REWARDS_PATH = "/gym/$venueUuid/loyalty/rewards";

// A preset belongs to its category, so switching category drops a preset image and makes the admin
// pick again. An uploaded image stays.
const CategoryInput = ({ presetUuids }: { presetUuids: Set<string> }) => {
  const { control, getValues, setValue } = useFormContext<RewardFormValues>();
  const { field, fieldState } = useController({ name: "category", control });

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>Category</FormLabel>
      <Select
        placeholder="Choose a category"
        value={field.value}
        onChange={(_, value) => {
          const image = getValues("image");
          if (value !== field.value && image && "uuid" in image && presetUuids.has(image.uuid)) {
            setValue("image", null, { shouldDirty: true });
          }
          field.onChange(value);
        }}
        onClose={field.onBlur}
      >
        {REWARD_CATEGORIES.map((category) => (
          <Option key={category} value={category}>
            {REWARD_CATEGORY_LABELS[category]}
          </Option>
        ))}
      </Select>
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

const VenuesInput = ({ system }: { system: ApiCreditSystemAdmin }) => {
  const { control } = useFormContext<RewardFormValues>();
  const { field, fieldState } = useController({ name: "venues", control });

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>Offered at</FormLabel>
      <Stack gap={1}>
        {system.venues.map((venue) => (
          <Checkbox
            key={venue.uuid}
            label={venue.name}
            checked={field.value.includes(venue.uuid)}
            onChange={(event) =>
              field.onChange(
                event.target.checked
                  ? [...field.value, venue.uuid]
                  : field.value.filter((uuid) => uuid !== venue.uuid),
              )
            }
          />
        ))}
      </Stack>
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

const DeleteRewardButton = ({
  reward,
  onDeleted,
}: {
  reward: ApiCreditProductAdmin;
  onDeleted: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const mutation = useDeleteCreditProductMutation(reward);

  return (
    <>
      <Button variant="plain" color="danger" onClick={() => setIsOpen(true)}>
        Delete
      </Button>
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ModalDialog sx={{ maxWidth: 440 }}>
          <DialogTitle>Delete {reward.name}?</DialogTitle>
          <DialogContent>
            Climbers won’t see it any more, and it’ll be gone from this list. Past redemptions are
            kept. If you might offer it again, disable it instead.
          </DialogContent>
          {mutation.isError ? <Alert color="danger">Couldn’t delete the reward.</Alert> : null}
          <DialogActions>
            <Button
              color="danger"
              loading={mutation.isPending}
              onClick={() => mutation.mutate(undefined, { onSuccess: onDeleted })}
            >
              Delete
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

const RewardForm = ({
  reward,
  system,
  venueUuid,
}: {
  reward?: ApiCreditProductAdmin;
  system: ApiCreditSystemAdmin;
  venueUuid: string;
}) => {
  const navigate = useNavigate();
  // Built once: the form resets itself from what the API returns after a save, and a background
  // refetch shouldn't wipe out edits in progress.
  const [defaultValues] = useState(() => rewardToFormValues(reward, venueUuid));
  const methods = useForm<RewardFormValues>({
    defaultValues,
    resolver: zodResolver(RewardFormSchema),
  });

  const { data: presets } = useCreditProductPresetImagesQuery();
  const presetUuids = useMemo(
    () => new Set(presets?.map((preset) => preset.image.uuid)),
    [presets],
  );

  // Read during render so react-hook-form tracks it. Skipped just before navigating away on
  // purpose, after a create or a delete.
  const skipBlocker = useUnsavedChanges("details", methods.formState.isDirty);

  const createMutation = useCreateCreditProductMutation(system.slug);
  const updateMutation = useUpdateCreditProductMutation(reward?.uuid);

  const closePanel = () => {
    skipBlocker();
    navigate({ to: REWARDS_PATH, params: { venueUuid } });
  };

  const onSubmit = async (values: RewardFormValues) => {
    const payload = rewardFormValuesToPayload(values);
    try {
      if (reward) {
        const updated = await updateMutation.mutateAsync(payload);
        methods.reset(rewardToFormValues(updated, venueUuid));
      } else {
        const created = await createMutation.mutateAsync(payload);
        skipBlocker();
        navigate({
          to: `${REWARDS_PATH}/$rewardUuid`,
          params: { venueUuid, rewardUuid: created.uuid },
          replace: true,
        });
      }
    } catch (error) {
      handleFormError(error, methods);
    }
  };

  const [category, cost] = methods.watch(["category", "cost"]);

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        autoComplete="off"
        onSubmit={methods.handleSubmit(onSubmit)}
        sx={{ flex: 1, minHeight: 0 }}
      >
        <Stack gap={2} sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <FormErrorAlert />
          <TextInput name="name" label="Name" />
          <TextareaInput name="description" label="Description" />
          <CategoryInput presetUuids={presetUuids} />
          <RewardImageInput category={category} presetUuids={presetUuids} />
          <Stack gap={0.5}>
            <NumberInput name="cost" label="Cost" endDecorator="credits" />
            {cost ? (
              <Typography level="body-sm" textColor="neutral.500">
                This works out as a value of{" "}
                <b>{formatCurrency(cost * system.creditValue, system.currency)}</b> based on a
                credit face value of{" "}
                {formatCurrency(system.creditValue, system.currency, { precise: true })}
              </Typography>
            ) : null}
          </Stack>
          {system.venues.length > 1 ? <VenuesInput system={system} /> : null}
          <Stack gap={0.5}>
            <CheckboxInput name="isActive" label="Enabled" />
            <Typography level="body-sm" textColor="neutral.500">
              Disabled rewards are hidden from climbers.
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          gap={1}
          sx={{ p: 2, borderTop: "1px solid", borderColor: "neutral.200" }}
        >
          <Button type="submit" loading={methods.formState.isSubmitting}>
            {reward ? "Save" : "Create reward"}
          </Button>
          {reward ? <DeleteRewardButton reward={reward} onDeleted={closePanel} /> : null}
        </Stack>
      </Stack>
    </FormProvider>
  );
};

export type RewardPanelSection = "stock" | "details";

// Stock and details for a saved reward, one open at a time like the competition route panel.
// Staff can stock a reward but not edit it, so they get its details to read.
const RewardSections = ({
  reward,
  system,
  venueUuid,
  isAdmin,
  section,
  onSectionChange,
}: {
  reward: ApiCreditProductAdmin;
  system: ApiCreditSystemAdmin;
  venueUuid: string;
  isAdmin: boolean;
  section: RewardPanelSection;
  onSectionChange: (section: RewardPanelSection) => void;
}) => (
  <AccordionGroup
    disableDivider
    sx={{
      flex: 1,
      minHeight: 0,
      ".MuiAccordion-root": { display: "flex", flexDirection: "column", minHeight: 0 },
      ".MuiAccordion-root.Mui-expanded": { flex: 1 },
      ".MuiAccordionDetails-root": { flex: 1, minHeight: 0 },
      ".MuiAccordionDetails-content": { padding: 0, minHeight: 0 },
      ".MuiAccordionSummary-root": { fontWeight: "bold", backgroundColor: "neutral.100" },
    }}
  >
    {/* Collapsed sections stay mounted, so edits to the details survive looking at the stock. */}
    <Accordion
      expanded={section === "stock"}
      onChange={(_, expanded) => onSectionChange(expanded ? "stock" : "details")}
    >
      <AccordionSummary>Stock</AccordionSummary>
      <AccordionDetails>
        <RewardStock reward={reward} system={system} venueUuid={venueUuid} />
      </AccordionDetails>
    </Accordion>
    <Accordion
      expanded={section === "details"}
      onChange={(_, expanded) => onSectionChange(expanded ? "details" : "stock")}
    >
      <AccordionSummary>Details</AccordionSummary>
      <AccordionDetails>
        {isAdmin ? (
          <RewardForm reward={reward} system={system} venueUuid={venueUuid} />
        ) : (
          <RewardSummary reward={reward} system={system} />
        )}
      </AccordionDetails>
    </Accordion>
  </AccordionGroup>
);

// The panel beside the rewards table, for a new reward or, given `rewardUuid`, an existing one.
// Admins add and edit rewards; staff can only stock them.
export default function RewardPanel({
  rewardUuid,
  section = "stock",
  onSectionChange,
}: {
  rewardUuid?: string;
  section?: RewardPanelSection;
  onSectionChange?: (section: RewardPanelSection) => void;
}) {
  const navigate = useNavigate();
  const { data: venue } = useCurrentVenueQuery();
  const isAdmin = venue?.userRole === "ADMIN";
  const canStock = isAdmin || venue?.userRole === "STAFF";
  const { data: system } = useVenueCreditSystemQuery(venue?.uuid);
  const rewardQuery = useCreditProductQuery(rewardUuid);

  const message = (alert: ReactNode) => <Stack sx={{ p: 2 }}>{alert}</Stack>;

  let content;
  if (venue && !rewardUuid && !isAdmin) {
    content = message(<Alert color="neutral">Only gym admins can add rewards.</Alert>);
  } else if (venue && !canStock) {
    content = message(<Alert color="neutral">Only gym admins and staff can manage rewards.</Alert>);
  } else if (rewardQuery.error instanceof HTTPError && rewardQuery.error.response.status === 404) {
    content = message(
      <Alert color="neutral">This reward doesn’t exist or has been deleted.</Alert>,
    );
  } else if (rewardQuery.error) {
    content = message(<Alert color="danger">Couldn’t load the reward.</Alert>);
  } else if (venue && system && !rewardUuid) {
    // A new reward has no stock until it's saved, so it's just the details.
    content = <RewardForm system={system} venueUuid={venue.uuid} />;
  } else if (venue && system && rewardQuery.data && rewardQuery.data.system !== system.slug) {
    // Switching gym keeps the reward open, so a reward from another credit system goes back to
    // this gym's list. So does an old link to one.
    return <Navigate to={REWARDS_PATH} params={{ venueUuid: venue.uuid }} replace />;
  } else if (venue && system && rewardQuery.data) {
    // Keyed so moving between rewards starts a fresh form and stock.
    content = (
      <RewardSections
        key={rewardUuid}
        reward={rewardQuery.data}
        system={system}
        venueUuid={venue.uuid}
        isAdmin={isAdmin}
        section={section}
        onSectionChange={(next) => onSectionChange?.(next)}
      />
    );
  } else {
    content = <CircularProgress sx={{ alignSelf: "center", mt: 4 }} />;
  }

  let title = "New reward";
  if (rewardUuid) {
    title = rewardQuery.data?.name ?? "Reward";
  }

  return (
    <UnsavedChangesProvider message="Discard your unsaved changes to this reward?">
      <Stack
        sx={{
          width: 420,
          flexShrink: 0,
          height: "100%",
          borderLeft: "1px solid",
          borderColor: "neutral.200",
          backgroundColor: "background.surface",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "neutral.200" }}
        >
          <Typography level="title-lg" noWrap sx={{ flex: 1 }}>
            {title}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => navigate({ to: REWARDS_PATH, params: { venueUuid: venue?.uuid ?? "" } })}
          >
            <Close />
          </IconButton>
        </Stack>
        {content}
      </Stack>
    </UnsavedChangesProvider>
  );
}
